import { useRef, useEffect } from 'react';
import { Sparkles, Send, StopCircle, AlertCircle } from 'lucide-react';
import { streamAi, type AiMessage, type StreamErrorInfo } from '@/lib/aiStream';
import { useTryIt } from '@/store/tryIt';
import { useAiPrefs } from '@/store/aiPrefs';
import {
  useAskAi,
  setInput,
  setStreamError,
  setStreaming,
  appendMessage,
  replaceLastMessage,
  popLastMessage,
  setAbortController,
  abortCurrentStream,
} from '@/store/askAi';
import { ModelPicker } from './ModelPicker';
import { LoginButton } from '@/components/playground/LoginButton';
import { cn } from '@/lib/cn';

/**
 * Streaming Ask-AI bar at the bottom of the main panel.
 *
 * - Cmd+J focuses (handled by app-level shortcut, see /lib/shortcuts.ts when
 *   that's wired up — Phase 2)
 * - System prompt is built from the currently active endpoint so questions
 *   like "what params does it need?" or "give me a Python example" land in
 *   context.
 * - Multi-turn conversation kept in component state for now; Phase 2 moves
 *   to a store with history persistence.
 */
/**
 * AskAiBar di-mount 2× (mobile bottom + desktop right panel). State
 * shared lewat useAskAi() store, refs (textarea/scroll) tetap local
 * karena DOM-specific. Cmd+J focus dispatch ke whichever instance lagi
 * visible — kita filter dengan offsetParent guard (offsetParent === null
 * berarti parent display:none, jadi skip focus).
 */
export function AskAiBar({ variant = 'bottom' }: { variant?: 'bottom' | 'right' } = {}) {
  const { input, messages, streamError, streaming } = useAskAi();
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const tryIt = useTryIt();
  const aiPrefs = useAiPrefs();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Global Cmd+J to focus this input, and external `playground:focus-ai`
  // events (fired by the command palette) to prefill + focus.
  // Both AskAiBar instances run this — masing-masing cuma focus kalau
  // sedang visible (offsetParent != null), jadi tidak ada race.
  useEffect(() => {
    const focusIfVisible = () => {
      const el = textareaRef.current;
      if (el && el.offsetParent !== null) el.focus();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        focusIfVisible();
      }
    };
    const onFocusAi = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === 'string' && detail.length > 0) {
        setInput(detail);
      }
      setTimeout(focusIfVisible, 50);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('playground:focus-ai', onFocusAi);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('playground:focus-ai', onFocusAi);
    };
  }, []);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const userMsg: AiMessage = { role: 'user', content: input.trim() };
    const nextMessages: AiMessage[] = [...messages, userMsg];
    appendMessage(userMsg);
    appendMessage({ role: 'assistant', content: '' });
    setInput('');
    setStreamError(null);
    setStreaming(true);

    const system = buildSystemPrompt(tryIt.endpoint);
    const ac = new AbortController();
    setAbortController(ac);

    let assistantText = '';
    await streamAi(
      { messages: nextMessages, system, model: aiPrefs.model, signal: ac.signal },
      {
        onDelta: (delta) => {
          assistantText += delta;
          replaceLastMessage({ role: 'assistant', content: assistantText });
        },
        onError: (err) => {
          popLastMessage(); // drop empty assistant placeholder
          setStreamError(err);
          setStreaming(false);
        },
        onDone: () => {
          setStreaming(false);
        },
      }
    );
  };

  const stop = () => {
    abortCurrentStream();
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void send();
    }
  };

  // Variant styling: 'bottom' = horizontal slim bar (mobile). 'right' = vertical
  // panel column yang fill remaining height di RightPanel (desktop).
  const isRight = variant === 'right';

  return (
    <div
      className={cn(
        isRight
          ? 'flex-1 flex flex-col min-h-0 bg-base'
          : 'border-t-[3px] border-border-strong bg-base'
      )}
    >
      {/* Right-variant header — kasih label "Ask AI" biar user tahu ini panel
          AI assistant, bukan code/response. Bottom-variant gak butuh karena
          location-nya udah self-explanatory di footer area. */}
      {isRight && (
        <div className="px-4 py-2.5 border-b-2 border-border-strong flex items-center gap-2 shrink-0">
          <Sparkles size={14} />
          <h3 className="text-sm font-display font-bold uppercase tracking-wider">Ask AI</h3>
          {tryIt.endpoint && (
            <span className="ml-auto text-2xs font-mono text-fg-tertiary truncate">
              {tryIt.endpoint.methodUpper} {tryIt.endpoint.path}
            </span>
          )}
        </div>
      )}

      {(messages.length > 0 || streamError) && (
        <div
          ref={scrollRef}
          className={cn(
            'overflow-y-auto px-4 py-3 space-y-3 border-b-2 border-border-strong',
            isRight ? 'flex-1' : 'max-h-72'
          )}
        >
          {messages.map((m, i) => (
            <MessageBubble key={i} role={m.role} content={m.content} />
          ))}
          {streamError && <ErrorCard error={streamError} onDismiss={() => setStreamError(null)} />}
        </div>
      )}

      {/* Spacer pas right-variant empty — biar input stick ke bawah */}
      {isRight && messages.length === 0 && !streamError && (
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <p className="text-2xs text-fg-tertiary leading-relaxed max-w-[240px]">
            Tanya apa pun tentang API. Pilih endpoint di sebelah kiri dulu biar AI tau konteksnya.
          </p>
        </div>
      )}

      <div className="px-4 py-3 shrink-0">
        <div className="flex items-center gap-2 rounded-pill border-[2.5px] border-border-strong bg-elevated pl-4 pr-2 shadow-brutal-sm focus-within:shadow-brutal transition-shadow">
          <Sparkles size={16} className="text-fg shrink-0" />
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder={
              tryIt.endpoint
                ? `Tanya soal ${tryIt.endpoint.methodUpper} ${tryIt.endpoint.path}...`
                : 'Ajukan pertanyaan...'
            }
            rows={1}
            className="flex-1 bg-transparent text-sm font-display placeholder:text-fg-tertiary focus:outline-none resize-none py-2.5 max-h-32"
          />
          {streaming ? (
            <button
              type="button"
              onClick={stop}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-pill border-2 border-border-strong bg-method-delete font-display font-bold text-2xs shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all"
              title="Stop"
            >
              <StopCircle size={12} />
              Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={send}
              disabled={!input.trim()}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 h-8 rounded-pill border-2 border-border-strong font-display font-bold text-2xs transition-all',
                input.trim()
                  ? 'bg-accent shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none'
                  : 'bg-overlay text-fg-tertiary opacity-60 cursor-not-allowed'
              )}
              title="Send (⌘ Enter)"
            >
              <Send size={11} />
              Send
            </button>
          )}
        </div>
        {/* Footer: context + shortcut + model picker.
            Mobile: hide context+kbd hint (terlalu noisy, gak muat), tampilin
            ModelPicker doang full-width supaya tetap accessible.
            Desktop: full info inline. */}
        <div className="mt-2 px-2 flex items-center justify-between gap-3 text-2xs text-fg-tertiary">
          <span className="hidden md:inline truncate flex-1">
            {tryIt.endpoint
              ? `Context: ${tryIt.endpoint.tag} → ${tryIt.endpoint.summary}`
              : 'Pilih endpoint biar AI tau konteksnya'}
            {' · '}
            <kbd className="font-mono px-1.5 py-0.5 bg-elevated border-2 border-border-strong rounded">⌘↵</kbd> to send
          </span>
          <div className="md:ml-auto">
            <ModelPicker />
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  role,
  content,
}: {
  role: 'user' | 'assistant' | 'system';
  content: string;
}) {
  const isUser = role === 'user';
  return (
    <div className={cn('flex gap-2', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-brutal-sm px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap border-2 border-border-strong shadow-brutal-sm',
          isUser ? 'bg-accent' : 'bg-elevated'
        )}
      >
        {content || <span className="inline-block w-1 h-3 bg-fg animate-pulse" />}
      </div>
    </div>
  );
}

/**
 * Card error untuk AI stream — friendly message + (untuk 'unauthorized')
 * embedded LoginButton supaya user gak perlu navigasi pulang ke landing.
 * Sengaja JANGAN render raw HTTP status / JSON detail — engineer-user
 * yang screenshot bisa accidentally leak cookie/token detail ke chat
 * thread / Slack / Twitter.
 */
function ErrorCard({
  error,
  onDismiss,
}: {
  error: StreamErrorInfo;
  onDismiss: () => void;
}) {
  // Color tint per error kind biar visual cue intuitive
  const bgClass =
    error.kind === 'unauthorized'
      ? 'bg-method-put' // yellow — needs action (login)
      : error.kind === 'network'
      ? 'bg-method-post' // sky — connectivity
      : 'bg-method-delete'; // coral — server / unknown

  return (
    <div
      className={cn(
        'rounded-brutal-sm border-2 border-border-strong shadow-brutal-sm px-3 py-2.5 flex items-start gap-2 text-sm',
        bgClass
      )}
    >
      <AlertCircle size={16} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold leading-tight">{error.message}</p>
        {error.kind === 'unauthorized' && (
          <div className="mt-2.5">
            <LoginButton variant="ghost" />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 text-2xs font-display font-bold underline opacity-70 hover:opacity-100 transition-opacity"
        title="Dismiss"
      >
        Tutup
      </button>
    </div>
  );
}

function buildSystemPrompt(endpoint: ReturnType<typeof useTryIt>['endpoint']): string {
  const base = [
    'You are an API integration assistant for the BalesOtomatis bo-chat3 backend.',
    'Help developers understand, call, and debug REST endpoints.',
    'Prefer concise answers with code blocks when relevant.',
    'When asked for code, use JavaScript fetch unless the user specifies otherwise.',
    'Always use Bearer auth: `Authorization: Bearer <jwt>` or rely on the `bo_session` cookie.',
  ];
  if (endpoint) {
    base.push(
      '',
      `Currently focused endpoint:`,
      `  Method: ${endpoint.methodUpper}`,
      `  Path:   ${endpoint.path}`,
      `  Tag:    ${endpoint.tag}`,
      `  Summary: ${endpoint.summary}`,
      ...(endpoint.description ? [`  Description: ${endpoint.description}`] : []),
      ...(endpoint.operation.parameters?.length
        ? [
            `  Parameters:`,
            ...endpoint.operation.parameters.map(
              (p) =>
                `    - ${p.name} (${p.in}${p.required ? ', required' : ''}): ${p.description || ''}`
            ),
          ]
        : [])
    );
  }
  return base.join('\n');
}
